// Dashboard — action-oriented hub (research-validated pattern):
// campus hero as contextual header, compact KPIs, prioritized task
// list and the simulated social feed. Frequent data stays <= 1 click.

import { useNavigate } from 'react-router-dom'
import { useGameStore } from '@/store/gameStore'
import { CLUB_LEVELS } from '@/config/clubLevelConfig'
import { REGION_META } from '@/config/regionConfig'
import { getTotalEstimatedIncome } from '@/game/systems/DemandSystem'
import { PageShell } from '../components/PageShell'
import { NextMatchHero } from '../components/dashboard/NextMatchHero'
import { TaskList } from '../components/dashboard/TaskList'
import { SocialFeed } from '../components/dashboard/SocialFeed'
import { GoalsTabContent } from '../panels/GoalsTab'
import { MechanicHint } from '../components/MechanicHint'
import { AnimatedNumber } from '../motion/AnimatedNumber'
import { Crown, Wallet, Users, LandPlot, TrendingUp, Landmark, ChevronRight } from 'lucide-react'

export function DashboardPage() {
  const navigate = useNavigate()
  const { day, week, month, money, clubLevel, courts, memberCounts, seasonObjective, boardConfidence, region } = useGameStore()
  const levelInfo = CLUB_LEVELS.find(l => l.level === clubLevel)
  const regionHero = REGION_META[region]?.heroImage
  const totalMembers = Object.values(memberCounts).reduce((sum, n) => sum + n, 0)
  const estimatedDaily = Math.round(getTotalEstimatedIncome())

  return (
    <PageShell
      pageId="dashboard"
      title="Despacho del Director"
      subtitle={`Dia ${day} · Semana ${week} · Mes ${month} · ${levelInfo?.name ?? 'Mi Club'}`}
      icon={<Crown size={22} />}
      backgroundImage={regionHero}
    >
      <div className="space-y-3">
        {/* Contextual hero: next league matchup as a broadcast teaser,
            or the campus itself before the league unlocks */}
        <NextMatchHero />

        {/* Board pressure: the season objective + confidence, jump to Carrera */}
        {seasonObjective && (
          <button
            onClick={() => navigate('/career')}
            className="row-tile"
            style={{
              width: '100%', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', textAlign: 'left',
              ...(boardConfidence < 30 ? { borderColor: 'color-mix(in srgb, var(--danger) 45%, transparent)' } : {}),
            }}
          >
            <Landmark size={16} style={{ color: 'var(--gold)', flexShrink: 0 }} />
            <div className="row-tile-main">
              <div className="row-tile-title">Junta: {seasonObjective.label}</div>
              <div className="row-tile-sub">
                {boardConfidence < 30 ? 'Confianza crítica — peligra tu puesto' : 'Confianza de la junta'}
              </div>
            </div>
            <div className="progress-track" style={{ width: '90px' }}>
              <div
                className={`progress-fill angled ${boardConfidence >= 55 ? 'success' : boardConfidence >= 30 ? 'warning' : 'danger'}`}
                style={{ width: `${boardConfidence}%` }}
              />
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          </button>
        )}

        {/* Compact KPIs */}
        <div className="kpi-grid">
          <Kpi
            icon={<Wallet size={14} />}
            label="Balance"
            value={<AnimatedNumber value={money} suffix="€" style={{ color: money >= 0 ? 'var(--success)' : 'var(--danger)' }} />}
          />
          <Kpi
            icon={<TrendingUp size={14} />}
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                Ingreso est./dia
                <MechanicHint
                  title="Demanda del dia"
                  bullets={[
                    'La demanda combina reputacion, clima, temporada, precio e instalaciones.',
                    'Las pistas indoor ignoran la lluvia; las outdoor la sufren.',
                    'Mas personal y edificios elevan la demanda base.',
                  ]}
                />
              </span>
            }
            value={<span style={{ color: 'var(--text-strong)' }}>{estimatedDaily.toLocaleString('es-ES')}€</span>}
          />
          <Kpi
            icon={<LandPlot size={14} />}
            label="Pistas"
            value={<span style={{ color: 'var(--text-strong)' }}>{courts.length}</span>}
          />
          <Kpi
            icon={<Users size={14} />}
            label="Socios"
            value={<span style={{ color: 'var(--text-strong)' }}>{totalMembers}</span>}
          />
        </div>

        {/* Tasks | Feed */}
        <div className="two-col">
          <TaskList />
          <SocialFeed />
        </div>

        {/* Metas del club — a compact teaser (next level + weekly goals);
            the full panel (incl. milestones) lives in Carrera → Metas. */}
        <GoalsTabContent compact />
      </div>
    </PageShell>
  )
}

function Kpi({ icon, label, value }: {
  icon: React.ReactNode
  label: React.ReactNode
  value: React.ReactNode
}) {
  return (
    <div className="stat-card tx-court-lines" style={{ padding: '0.6rem 0.75rem' }}>
      <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.2rem' }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: '1.15rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  )
}
